interface PickedAsset {
  base64?: string | null;
  mimeType?: string;
  uri: string;
}
type PickResult = { canceled: true } | { canceled: false; assets?: PickedAsset[] };

export interface PickVibeImageDeps {
  pick?: () => Promise<PickResult>;
  /** Re-encode the picked file as JPEG and hand back its base64. */
  toJpeg?: (uri: string) => Promise<{ base64?: string | null }>;
}

const defaultPick = async (): Promise<PickResult> => {
  const ImagePicker = await import('expo-image-picker');
  // launchImageLibraryAsync uses the out-of-process PHPicker on iOS — no permission gate.
  return ImagePicker.launchImageLibraryAsync({ mediaTypes: ['images'], quality: 0.6, base64: true });
};

const defaultToJpeg = async (uri: string) => {
  const { ImageManipulator, SaveFormat } = await import('expo-image-manipulator');
  const rendered = await ImageManipulator.manipulate(uri).resize({ width: 1024 }).renderAsync();
  return rendered.saveAsync({ format: SaveFormat.JPEG, compress: 0.6, base64: true });
};

/**
 * Pick one image from the library and return a JPEG data-URI (base64) for the plan-first
 * vision call — or null if cancelled/unavailable.
 *
 * The re-encode is the point, and it is unconditional. `quality: 0.6` looks like it should
 * already produce a JPEG, and for most formats it does; but expo-image-picker's iOS handler
 * has `case UTType.heic.identifier: return (rawData, ".heic")` — HEIC (and TIFF, and AVIF)
 * comes back byte-for-byte as it was, labelled `image/heic`. Every iPhone since iOS 11 shoots
 * HEIC by default, so "Match a picture" answered `ValueError: Image must be jpg, png, gif, or
 * webp` for most real photographs while working perfectly for every screenshot — which is
 * exactly the photo anybody testing it reaches for.
 *
 * Converting ALWAYS rather than only for the formats known to fail keeps the list of what the
 * model accepts out of this file: it lives at Replicate, it can change, and a copy here would
 * be one more thing to keep in sync. The resize is not cosmetic either — the request travels
 * inside a Lambda Function URL invocation, and an untouched 12-megapixel photo is a payload
 * this call has no use for.
 */
export async function pickVibeImage(deps: PickVibeImageDeps = {}): Promise<string | null> {
  const pick = deps.pick ?? defaultPick;
  const toJpeg = deps.toJpeg ?? defaultToJpeg;

  let asset: PickedAsset | undefined;
  try {
    const result = await pick();
    asset = !result.canceled ? result.assets?.[0] : undefined;
  } catch {
    return null;
  }
  if (!asset?.base64) return null;

  try {
    const jpeg = await toJpeg(asset.uri);
    if (jpeg.base64) return `data:image/jpeg;base64,${jpeg.base64}`;
  } catch {
    /*
     * Falling through on purpose. A converter that could not run is a fact about the moment,
     * not about the picture — and the common case underneath this fallback is a photo that
     * was already a JPEG, which the model reads perfectly well. A HEIC that lands here still
     * fails, visibly, with a reason; it is not turned into silence.
     */
  }
  return `data:${asset.mimeType ?? 'image/jpeg'};base64,${asset.base64}`;
}
